/**
 * Contractor Welcome Page
 * Shown to new Contractors after email verification / onboarding
 * Guides them to complete profile, browse jobs, and submit proposals
 */

import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Person,
  Search,
  AutoAwesome,
  ArrowForward,
  Explore
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { brand, text, bg, border, shadow, easing } from '../theme/colors';

const ContractorWelcomePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const firstName = user?.profile?.firstName || user?.first_name || 'there';
  const profileComplete = user?.profile?.bio && user?.profile?.city;

  const markWelcomeSeen = useCallback(async () => {
    try {
      await apiClient.put('/auth/users/profile', { welcome_seen: true });
      updateUser({
        profile: {
          ...user?.profile,
          welcomeSeen: true,
        },
      });
      localStorage.setItem('eqaya_contractor_welcome_shown', 'true');
    } catch {
      // Non-fatal
    }
  }, [updateUser, user?.profile]);

  useEffect(() => {
    if (!user?.profile?.welcomeSeen && user?.profile?.welcome_seen !== true) {
      markWelcomeSeen();
    } else {
      localStorage.setItem('eqaya_contractor_welcome_shown', 'true');
    }
  }, [markWelcomeSeen, user?.profile?.welcomeSeen, user?.profile?.welcome_seen]);

  const handleCompleteProfile = async () => {
    await markWelcomeSeen();
    navigate('/profile');
  };

  const handleBrowseJobs = async () => {
    await markWelcomeSeen();
    navigate('/contractor/marketplace');
  };

  const handleExplore = async () => {
    await markWelcomeSeen();
    navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: `1px solid ${border.light}`,
          boxShadow: shadow.md,
          borderRadius: 3,
        }}
      >
        {/* Welcome header */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: `${brand.primary}14`,
              mb: 2,
            }}
          >
            <AutoAwesome sx={{ fontSize: 32, color: brand.primary }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: text.primary }} gutterBottom>
            Welcome, {firstName}!
          </Typography>
          <Typography variant="body1" sx={{ color: text.secondary }}>
            Your contractor account is ready. Here's how to start winning work.
          </Typography>
        </Box>

        {/* How it works */}
        <Box sx={{ mb: 4, p: 3, bgcolor: bg.subtle, borderRadius: 2, border: `1px solid ${border.light}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: text.primary, mb: 2, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            How It Works for Contractors
          </Typography>
          {[
            { num: '1', label: 'Complete Your Profile', desc: 'Add skills, certifications, and past work to stand out' },
            { num: '2', label: 'Find Jobs & Quote in Stages', desc: 'Browse projects, ask to assess, and send your quotation split into stages. Clients say yes to "start with USD 700", not to a lump sum — and you never start an unfunded stage.' },
            { num: '3', label: 'Get Hired & Get Paid', desc: 'The client funds each stage before you start; you are paid as each stage is approved' },
            { num: '4', label: 'Build Your Name on Eqaya', desc: 'Only jobs paid through the Eqaya wallet count towards your reviews, job count and verified profile. A cash job builds nothing.' },
          ].map((step, i, arr) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: i < arr.length - 1 ? 2 : 0 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%',
                bgcolor: brand.primary, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, mt: 0.25,
              }}>
                {step.num}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" sx={{ color: text.secondary }}>
                  {step.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: text.primary }}>
          What would you like to do first?
        </Typography>

        {/* Primary Action - Complete Profile (if incomplete) or Browse Jobs */}
        {!profileComplete ? (
          <Card
            sx={{
              mb: 2,
              cursor: 'pointer',
              border: `2px solid ${brand.primary}`,
              boxShadow: shadow.sm,
              transition: `transform 0.2s ${easing.smooth}, box-shadow 0.2s ${easing.smooth}`,
              '&:hover': {
                bgcolor: `${brand.primary}06`,
                transform: 'translateY(-2px)',
                boxShadow: shadow.md,
              },
              '&:active': { transform: 'translateY(0)' },
            }}
            onClick={handleCompleteProfile}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                background: brand.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Person sx={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>
                  Complete Your Profile
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  Takes about 2 minutes — add your skills and experience to get noticed
                </Typography>
              </Box>
              <ArrowForward sx={{ color: brand.primary }} />
            </CardContent>
          </Card>
        ) : (
          <Card
            sx={{
              mb: 2,
              cursor: 'pointer',
              border: `2px solid ${brand.primary}`,
              boxShadow: shadow.sm,
              transition: `transform 0.2s ${easing.smooth}, box-shadow 0.2s ${easing.smooth}`,
              '&:hover': {
                bgcolor: `${brand.primary}06`,
                transform: 'translateY(-2px)',
                boxShadow: shadow.md,
              },
              '&:active': { transform: 'translateY(0)' },
            }}
            onClick={handleBrowseJobs}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                background: brand.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Search sx={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>
                  Browse Available Jobs
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  Find construction projects that match your expertise
                </Typography>
              </Box>
              <ArrowForward sx={{ color: brand.primary }} />
            </CardContent>
          </Card>
        )}

        {/* Secondary Action - Browse Jobs (if profile incomplete) or AI Proposals */}
        <Card
          sx={{
            mb: 2,
            cursor: 'pointer',
            border: `1px solid ${border.light}`,
            transition: `transform 0.2s ${easing.smooth}, border-color 0.2s ${easing.smooth}`,
            '&:hover': {
              borderColor: brand.secondary,
              bgcolor: `${brand.secondary}06`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          }}
          onClick={!profileComplete ? handleBrowseJobs : handleCompleteProfile}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: `${brand.secondary}12`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {!profileComplete
                ? <Search sx={{ color: brand.secondary }} />
                : <Person sx={{ color: brand.secondary }} />
              }
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary }}>
                {!profileComplete ? 'Browse Jobs First' : 'Update Your Profile'}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                {!profileComplete
                  ? 'See what projects are available before completing your profile'
                  : 'Add certifications, portfolio photos, and more to stand out'
                }
              </Typography>
            </Box>
            <ArrowForward sx={{ color: text.tertiary }} />
          </CardContent>
        </Card>

        {/* Tertiary Action - Go to Dashboard */}
        <Card
          sx={{
            cursor: 'pointer',
            border: `1px solid ${border.light}`,
            transition: `border-color 0.2s ${easing.smooth}`,
            '&:hover': {
              borderColor: border.default,
              bgcolor: bg.subtle,
            },
          }}
          onClick={handleExplore}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: bg.subtle,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Explore sx={{ color: text.tertiary }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary }}>
                Go to Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Explore the platform at your own pace
              </Typography>
            </Box>
            <ArrowForward sx={{ color: text.disabled }} />
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" sx={{ color: text.tertiary }} textAlign="center" display="block">
          You can always find jobs from the marketplace in your dashboard
        </Typography>
      </Paper>
    </Container>
  );
};

export default ContractorWelcomePage;
